import { useDebouncedCallback, usePrevious } from "@react-hookz/web";
import { useCallback, useEffect, useRef } from "react";
/**
 * Wraps common functionality up around ResizeObservers.
 */
export function useResizeObserver(onResize) {
  let {
    debounceMaxMs = undefined,
    debounceMs = 0,
    enabled = true
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const observerRef = useRef(null);
  const observedElement = useRef(null);
  const debounceEnabled = debounceMs > 0;
  const debouncedOnResize = useDebouncedCallback(onResize, [onResize], debounceMs, debounceMaxMs);
  const callback = debounceEnabled ? debouncedOnResize : onResize;
  const fnRef = useCallback(element => {
    observedElement.current = element;
    if (observerRef.current) {
      // If we already had a ResizeObserver, clean up and replace it.
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (window.ResizeObserver && element && enabled) {
      observerRef.current = new ResizeObserver(function () {
        callback(...arguments);
      });
      observerRef.current.observe(element);
    }
  }, [callback, enabled]);

  // If enabled changes, turn on/off the ResizeObserver.
  const previousEnabled = usePrevious(enabled);
  useEffect(() => {
    if (observerRef.current && observedElement.current) {
      if (!previousEnabled && enabled) {
        observerRef.current.observe(observedElement.current);
      } else if (previousEnabled && !enabled) {
        observerRef.current.unobserve(observedElement.current);
      }
    }
  }, [enabled, previousEnabled]);

  // Turn off the observer when we unmount.
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
  return {
    ref: fnRef
  };
}
//# sourceMappingURL=useResizeObserver.js.map